// src/onboarding/useOnboardingPoller.js
//
// The ONE poller of the personalization status (decision 5), mounted by AppShell.
// Cadence (decision 6): 10 s while running or while a start is pending, 30 s while degraded or
// after a failed read, 5 min otherwise — only while the tab is visible — plus a re-read on window
// focus (throttled). ⛔ Nothing runs unless `enabled` (an admin of the ACTIVE workspace, not a
// superadmin, phase 'ready'): a 401 from a poll would sign the user out.
//
// On every 200 it (1) decides the outcome of the run the user was waiting for (decision 15),
// (2) bumps the runFinished epoch + refreshes the wallet when a run ended (decision 16 — the run
// spends credits) and (3) reads the brand document ONCE, lazily, when the state would prompt
// (decision 10) — to learn whether the workspace can EVER be personalized. "Once" is per
// (workspace, login, page load): the effect is keyed on tenantId + loginKey, and either change
// resets the store.
import { useEffect, useRef } from 'react';
import { getOnboardingStatus } from './onboardingApi.js';
import { getBrandDocument } from '../context/brandIdentityApi.js';
import { refreshEntitlements } from '../hooks/useEntitlements.js';
import {
  viewOf, pollDelay, detectOutcome, PROMPT_VIEWS, FOCUS_THROTTLE_MS,
  personalizableFromDocument, personalizableFromError,
} from './onboardingModel.js';
import { readPendingStart, clearPendingStart, tokenKey } from './onboardingStorage.js';
import {
  getOnboardingSnapshot, patchOnboarding, resetOnboarding, bumpRunFinished, registerOnboardingRefresher,
} from './onboardingStore.js';

export default function useOnboardingPoller({ enabled, tenantId, token }) {
  const loginKey = tokenKey(token);
  const timerRef = useRef(null);
  const lastFetchRef = useRef(0);

  useEffect(() => {
    if (!enabled || !tenantId) {
      resetOnboarding(null);
      return undefined;
    }
    resetOnboarding(tenantId);
    let alive = true;
    let checking = false;
    let inFlight = false;

    const clear = () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
    const schedule = (failed = false) => {
      clear();
      const snap = getOnboardingSnapshot();
      const delay = pollDelay(snap.status, { pendingStart: snap.pendingStart, failed });
      timerRef.current = setTimeout(() => tick(), delay);
    };

    const checkPersonalizable = async () => {
      const snap = getOnboardingSnapshot();
      if (checking || snap.personalizableChecked || !PROMPT_VIEWS.includes(viewOf(snap.status))) return;
      checking = true;
      try {
        const doc = await getBrandDocument({ tenant: tenantId });
        if (alive) patchOnboarding({ personalizable: personalizableFromDocument(doc), personalizableChecked: true });
      } catch (err) {
        if (alive) patchOnboarding({ personalizable: personalizableFromError(err), personalizableChecked: true });
      } finally {
        checking = false;
      }
    };

    async function tick() {
      if (!alive) return;
      if (typeof document !== 'undefined' && document.hidden) { schedule(); return; }
      if (inFlight) return;
      inFlight = true;
      lastFetchRef.current = Date.now();
      try {
        const next = await getOnboardingStatus({ tenant: tenantId });
        if (!alive) return;
        const prev = getOnboardingSnapshot();
        const pending = readPendingStart(tenantId);
        const res = detectOutcome(prev.lastGood, next, pending, Date.now());
        if (res.clearPending) clearPendingStart();
        patchOnboarding({
          status: next,
          lastGood: next.degraded ? prev.lastGood : next,
          error: null,
          pendingStart: !!pending && !res.clearPending,
          // A start that is now RUNNING drops any older notice (e.g. a previous "not confirmed").
          outcome: res.outcome
            ? { kind: res.outcome, code: res.code || null, at: Date.now() }
            : (res.clearPending ? null : prev.outcome),
          // Needs no read when the state does not prompt.
          personalizableChecked: prev.personalizableChecked || !PROMPT_VIEWS.includes(viewOf(next)),
        });
        if (res.finished) {
          bumpRunFinished();
          refreshEntitlements();
        }
        inFlight = false;
        await checkPersonalizable();
        schedule();
      } catch (err) {
        inFlight = false;
        if (!alive) return;
        patchOnboarding({ error: err });
        schedule(true);
      }
    }

    const onVisibility = () => { if (!document.hidden) tick(); };
    const onFocus = () => {
      if (Date.now() - lastFetchRef.current >= FOCUS_THROTTLE_MS) tick();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    const unregister = registerOnboardingRefresher(() => tick());
    tick();

    return () => {
      alive = false;
      clear();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      unregister();
    };
  }, [enabled, tenantId, loginKey]);
}
